import { useEffect, useRef, useState } from 'react';
import { useCuttingStore } from './useCuttingStore';

/**
 * Block 14 «Раскрой» — CUT-1.6 автосохранение (PR #4-SHELL).
 *
 * Каждые `intervalMs` (канон TRD CUT-1.6 = **5с**; 8с в прототипе app.js:823 —
 * имитация) при наличии правок (`dirty`) текущий документ автосохраняется как
 * черновик через стор/клиент (`autosaveDraft` → `saveDraftToServer`). Без правок
 * клиент НЕ дёргается. Индикатор-текст — вербатим app.js: `💾 сохр.` → через
 * 800мс `✓ Nс назад` / `Nм назад`. Таймеры чистятся при unmount (без утечек).
 *
 * Возвращает строку для индикатора `.sc-autosave` в шапке.
 */

export const AUTOSAVE_INTERVAL_MS = 5000; // TRD CUT-1.6
const SAVED_FLIP_MS = 800; // app.js:822 — задержка смены «сохр.» → «✓ … назад»
export const AUTOSAVE_IDLE_LABEL = '💾 сохр.';

/**
 * `✓ Nc назад` / `✓ Nм назад` (вербатим формат app.js:821 — секунды латинской `c`,
 * минуты кириллической `м`, как в прототипе).
 */
export function agoLabel(savedAt: number, now: number = Date.now()): string {
  const sec = Math.max(0, Math.floor((now - savedAt) / 1000));
  const unit = sec < 60 ? `${sec}c` : `${Math.floor(sec / 60)}м`;
  return `✓ ${unit} назад`;
}

export function useAutosave(intervalMs: number = AUTOSAVE_INTERVAL_MS): string {
  const [label, setLabel] = useState<string>(AUTOSAVE_IDLE_LABEL);
  const savedAtRef = useRef<number | null>(null);
  const flipRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let mounted = true;
    const tick = () => {
      const store = useCuttingStore.getState();
      if (!store.dirty) {
        // Нет правок — клиент не дёргаем; обновляем «N назад», если уже сохраняли.
        if (savedAtRef.current !== null) setLabel(agoLabel(savedAtRef.current));
        return;
      }
      setLabel(AUTOSAVE_IDLE_LABEL); // «💾 сохр.» на время сохранения
      void store.autosaveDraft().then(() => {
        if (!mounted) return; // unmount во время in-flight сохранения — не трогаем state
        flipRef.current = setTimeout(() => {
          if (!mounted) return;
          savedAtRef.current = Date.now();
          setLabel(agoLabel(savedAtRef.current));
        }, SAVED_FLIP_MS);
      });
    };

    const interval = setInterval(tick, intervalMs);
    return () => {
      mounted = false;
      clearInterval(interval);
      if (flipRef.current !== null) clearTimeout(flipRef.current);
    };
  }, [intervalMs]);

  return label;
}
